/**
 * Visual map of how clarity / strategy artifacts connect in the product.
 * Mirrors the canonical methodology diagram: ambient open questions, driver → outcome,
 * then parallel Back Plan vs OST, all traceable into prioritization items.
 */

import { Link } from 'react-router-dom';
import {
  ListTree,
  ArrowDown,
  ArrowRight,
} from 'lucide-react';
import type { StrategyMapCardId } from './strategyConnectivityTypes';

function MapCard({
  mapId,
  title,
  subtitle,
  href,
  accent,
  highlightCardId,
}: {
  mapId: StrategyMapCardId;
  title: string;
  subtitle: string;
  href: string;
  accent: 'slate' | 'violet' | 'amber' | 'purple' | 'teal' | 'emerald';
  highlightCardId?: StrategyMapCardId | null;
}) {
  const ring =
    accent === 'violet'
      ? 'border-[var(--slate)] dark:border-[var(--slate)] hover:border-[var(--slate)] bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/20'
      : accent === 'amber'
        ? 'border-[var(--mustard)] dark:border-[var(--mustard)] hover:border-[var(--mustard)] bg-[var(--mustard-dim)]/50 dark:bg-[var(--mustard)]/20'
        : accent === 'purple'
          ? 'border-[var(--slate)] dark:border-[var(--slate)] hover:border-[var(--slate)] bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/20'
          : accent === 'teal'
            ? 'border-[var(--green)] dark:border-[var(--green)] hover:border-[var(--green)] bg-[var(--green-dim)]/50 dark:bg-[var(--green)]/20'
            : accent === 'emerald'
              ? 'border-[var(--green)] dark:border-[var(--green)] hover:border-[var(--green)] bg-[var(--green-dim)]/50 dark:bg-[var(--green)]/20'
              : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40';

  const hi = highlightCardId === mapId;

  return (
    <Link
      to={href}
      className={`block  border px-4 py-3 transition-colors ${ring} ${
        hi ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-[var(--surface)] z-[1] relative' : ''
      }`}
    >
      <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{title}</p>
      <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{subtitle}</p>
    </Link>
  );
}

function Edge({ label, dashed }: { label: string; dashed?: boolean }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-0.5 py-1 text-center ${
        dashed ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'
      }`}
    >
      <ArrowDown className={`h-4 w-4 ${dashed ? 'opacity-60' : ''}`} />
      <span className={`text-[10px] uppercase tracking-wide max-w-[14rem] leading-tight ${dashed ? 'border-b border-dashed border-[var(--rule)]' : ''}`}>
        {label}
      </span>
    </div>
  );
}

function HorizEdge({ label, dashed }: { label: string; dashed?: boolean }) {
  return (
    <div
      className={`hidden lg:flex flex-col items-center justify-center min-w-[4rem] text-center ${
        dashed ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'
      }`}
    >
      <ArrowRight className={`h-4 w-4 mb-0.5 ${dashed ? 'opacity-60' : ''}`} />
      <span className={`text-[10px] uppercase tracking-wide leading-tight px-1 ${dashed ? 'border-b border-dashed' : ''}`}>
        {label}
      </span>
    </div>
  );
}

export interface StrategyClarityMapProps {
  /** Highlights one methodology card (ring). */
  highlightCardId?: StrategyMapCardId | null;
  /** When false, skips the title and intro paragraphs (e.g. embedded in a modal that already explains context). */
  showIntro?: boolean;
}

export function StrategyClarityMap({
  highlightCardId = null,
  showIntro = true,
}: StrategyClarityMapProps) {
  const inner = (
    <div className="space-y-2 lg:space-y-0">
      {/* Row: Open questions */}
      <MapCard
        mapId="open-questions"
        title="Open questions"
        subtitle="What we don’t know yet — can surface while working on anything below."
        href="/prioritization/open-questions"
        accent="slate"
        highlightCardId={highlightCardId}
      />
      <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-2 lg:gap-3 lg:items-stretch">
        <div className="hidden lg:block" />
        <div className="flex flex-col gap-1 py-1">
          <Edge label="Can anchor to items / cycles / sessions / tracks" dashed />
          <Edge label="Can surface at any stage (ambient)" dashed />
        </div>
        <div className="hidden lg:block" />
      </div>

      {/* Driver → Desired outcome */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <MapCard
          mapId="outcome-driver"
          title="Outcome driver"
          subtitle="Why change is needed — frames the outcome space."
          href="/prioritization/outcome-drivers"
          accent="violet"
          highlightCardId={highlightCardId}
        />
        <HorizEdge label="Establishes the why (canonical)" />
        <MapCard
          mapId="desired-outcome"
          title="Desired outcome"
          subtitle="Who wants what, evidence of success, obstacles, steps."
          href="/prioritization/desired-outcomes"
          accent="amber"
          highlightCardId={highlightCardId}
        />
      </div>
      <div className="flex justify-center lg:justify-end lg:pr-[calc(50%+0.5rem)] py-1">
        <span className="text-[10px] text-[var(--text-ter)] uppercase tracking-wide">
          Also: names opportunity space for OST (natural, not forced)
        </span>
      </div>

      {/* Branch: Back plan | OST from desired outcome */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
        <div>
          <Edge label="Goal node & backward reasoning" />
          <MapCard
            mapId="backplan"
            title="Back plan"
            subtitle="What must be true, working backward from success."
            href="/prioritization/backplan"
            accent="teal"
            highlightCardId={highlightCardId}
          />
          <div className="flex justify-center py-0.5">
            <span className="text-[10px] text-[var(--text-ter)] uppercase tracking-wide">
              Planning module · same graph, forward framing
            </span>
          </div>
          <MapCard
            mapId="roadmap"
            title="Roadmap"
            subtitle="Planning mode: conditions and milestones from now toward the goal."
            href="/planning/roadmap"
            accent="emerald"
            highlightCardId={highlightCardId}
          />
          <Edge label="Nodes link to items" />
        </div>
        <div>
          <Edge label="Root outcome = product outcome (Teresa Torres)" />
          <MapCard
            mapId="ost"
            title="Opportunity solution tree"
            subtitle="Outcomes → opportunities → solutions → experiments."
            href="/prioritization/strategy"
            accent="purple"
            highlightCardId={highlightCardId}
          />
          <Edge label="Solution nodes link to items" />
        </div>
      </div>

      <div
        className={`flex flex-wrap items-center justify-center gap-2 pt-4 border-t border-[var(--rule)] ${
          highlightCardId === 'prioritization-items' ? 'ring-2 ring-primary-500  ring-offset-2 dark:ring-offset-[var(--surface)] py-3' : ''
        }`}
      >
        <ListTree className="h-4 w-4 text-[var(--green)]" />
        <Link
          to="/prioritization/items"
          className="inline-flex items-center gap-2 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)]/80 dark:bg-[var(--green)]/30 px-4 py-2.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/40"
        >
          Prioritization items
          <span className="text-xs font-normal opacity-80">— where work is tracked</span>
        </Link>
      </div>

      <p className="text-center text-xs text-[var(--text-ter)] pt-2">
        Classic alignment <span className="mx-1">·</span>{' '}
        <Link className="text-primary-600 hover:underline dark:text-primary-400" to="/prioritization/strategy/list">Strategies</Link>
        <span className="mx-1">→</span>
        <Link className="text-primary-600 hover:underline dark:text-primary-400" to="/prioritization/strategy/objectives">Objectives</Link>
        <span className="mx-1">→</span>
        <Link className="text-primary-600 hover:underline dark:text-primary-400" to="/prioritization/strategy/goals">Goals</Link>
        <span className="mx-1">·</span>
        <Link className="text-primary-600 hover:underline dark:text-primary-400" to="/prioritization/strategy/kernel">Strategy kernel</Link>
        <span className="mx-1">·</span>
        <Link className="text-primary-600 hover:underline dark:text-primary-400" to="/prioritization/strategy/canvas">Strategy canvas</Link>
      </p>
    </div>
  );

  if (!showIntro) {
    return (
      <div className="border border-[var(--rule)] bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-900/40 dark:to-[var(--surface)] p-4 sm:p-5">
        {inner}
      </div>
    );
  }

  return (
    <section
      id="strategy-clarity-map"
      className="border border-[var(--rule)] bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-900/40 dark:to-[var(--surface)] p-5 sm:p-6"
      aria-labelledby="strategy-map-heading"
    >
      <div className="mb-5">
        <h2 id="strategy-map-heading" className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          How these pieces connect
        </h2>
        <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] max-w-3xl">
          Open questions are ambient. The only <span className="font-medium text-[var(--text-sec)]">canonical</span>{' '}
          sequence is <span className="font-medium">Outcome Driver → Desired Outcome</span>. Back Plan and Opportunity Solution
          Tree are parallel paths from a desired outcome into execution and discovery; both link to work as{' '}
          <Link to="/prioritization/items" className="text-primary-600 hover:text-primary-700 dark:text-primary-400">
            prioritization items
          </Link>{' '}
          via methodology connections.
        </p>
        <p className="mt-2 text-xs text-[var(--text-ter)]">
          Dashed cues in the diagram = optional / situational / non-mandatory links in the methodology.
        </p>
      </div>

      {inner}
    </section>
  );
}
